'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from '@material-bank/i18n';
import { MTAG_EVENTS, SOURCE_APP, omitEmpty, trackBeforeNavigating, useAnalytics } from '@material-bank/mtag-analytics';
import { useAuthProvider } from '../../providers/AuthProvider';
import { useRegisterRedirect } from '../../hooks/useRegisterRedirect';
import { useMb2Redirect } from '../../hooks/useMb2Redirect';
import type { LoginStep } from './PasswordlessLogin';
function originatingUrl(): string {
  return typeof window !== 'undefined' ? window.location.href : '';
}
type UsePasswordlessLoginProps = {
  onSuccess: () => void;
  onStepChange: (step: LoginStep) => void;
};
const ERROR_TYPES: Record<string, string> = {
  OTP_EXPIRED_OR_INVALID: 'otpExpiredOrInvalid',
  OTP_SEND_BLOCKED: 'otpSendBlocked'
};
export function usePasswordlessLogin({
  onSuccess,
  onStepChange
}: UsePasswordlessLoginProps) {
  const t = useTranslations('authentication.dialog');
  const {
    status,
    user,
    error,
    initiateLogin,
    verifyOtp,
    clearError
  } = useAuthProvider();
  const {
    canRedirect,
    redirectToRegister
  } = useRegisterRedirect();
  const {
    canRedirect: canRedirectMb2,
    redirectToMb2
  } = useMb2Redirect();
  const {
    track,
    identify,
    isReady
  } = useAnalytics();
  const [email, setEmail] = useState('');
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const emailStartedRef = useRef(false);
  const otpStartedRef = useRef(false);
  const signedInRef = useRef(false);
  const registrationStartedRef = useRef(false);
  const mb2RedirectedRef = useRef(false);
  // The OTP error already reported, so one rejected code fires one `failed` even
  // though the effect re-runs on every render.
  const reportedOtpErrorRef = useRef<string | null>(null);

  // The sign-in flow opens on the email step. Wait for the SDK to be ready
  // before firing; otherwise track() no-ops and the fire-once ref below would
  // suppress the retry once isReady flips true.
  useEffect(() => {
    if (!isReady || emailStartedRef.current) {
      return;
    }
    emailStartedRef.current = true;
    track(MTAG_EVENTS.SIGN_IN_STEP, {
      source_app: SOURCE_APP,
      step_name: 'EMAIL',
      step_status: 'started',
      url: originatingUrl()
    });
  }, [isReady, track]);
  useEffect(() => {
    if (status === 'awaiting-otp') {
      if (isReady && !otpStartedRef.current) {
        otpStartedRef.current = true;
        track(MTAG_EVENTS.SIGN_IN_STEP, omitEmpty({
          source_app: SOURCE_APP,
          step_name: 'EMAIL',
          step_status: 'complete',
          email,
          url: originatingUrl()
        }));
        track(MTAG_EVENTS.SIGN_IN_STEP, {
          source_app: SOURCE_APP,
          step_name: 'checkEmail',
          step_status: 'started',
          url: originatingUrl()
        });
      }

      // A rejected code leaves the status on `awaiting-otp` and surfaces the verify
      // error; a failed *send* lands on `unauthenticated` instead, so an error here is
      // always the code being wrong.
      if (isReady && error && reportedOtpErrorRef.current !== error) {
        reportedOtpErrorRef.current = error;
        track(MTAG_EVENTS.SIGN_IN_STEP, {
          source_app: SOURCE_APP,
          step_name: 'checkEmail',
          step_status: 'failed',
          url: originatingUrl()
        });
      }
      onStepChange('otp');
    }

    // Every submit clears the error first, so releasing the latch here is what lets the
    // same wrong code entered twice report two failures rather than one.
    if (!error && reportedOtpErrorRef.current !== null) {
      reportedOtpErrorRef.current = null;
    }
    if (status === 'authenticated') {
      if (isReady && !signedInRef.current) {
        signedInRef.current = true;
        track(MTAG_EVENTS.SIGN_IN_STEP, {
          source_app: SOURCE_APP,
          step_name: 'checkEmail',
          step_status: 'complete',
          url: originatingUrl()
        });
        // Re-establish the authenticated user on the SDK before signed_in fires.
        if (user?.id) {
          identify(user.id);
        }
        track(MTAG_EVENTS.SIGNED_IN, {
          source_app: SOURCE_APP,
          url: originatingUrl()
        });
      }
      onSuccessRef.current();
    }

    // The identity verified but has no MB3 account — send it to registration.
    if (status === 'registration-required' && canRedirect) {
      // MTag's only step in the account-creation flow: the hand-off to MB3,
      // which finishes the rest of the steps.
      if (isReady && !registrationStartedRef.current) {
        registrationStartedRef.current = true;
        const delivery = track(MTAG_EVENTS.CREATE_ACCOUNT_STEP, {
          source_app: SOURCE_APP,
          step_name: 'EMAIL',
          step_status: 'started',
          url: originatingUrl()
        });
        trackBeforeNavigating(delivery, redirectToRegister);
      } else if (!registrationStartedRef.current) {
        // Analytics is not ready, so redirect without the event. The ref is set for
        // the same reason as above: this effect re-runs on every render (onStepChange
        // is a fresh reference each time), so without it a re-render — or `isReady`
        // flipping true before the navigation settles — would take the branch above
        // and both fire create_account_step and redirect a second time.
        registrationStartedRef.current = true;
        redirectToRegister();
      }
    }

    // A non-migrated MB2 account can't enter mtag — bounce it to MB2 login when
    // a target is configured; otherwise the message below is shown.
    if (status === 'mb2-migration-required' && canRedirectMb2 && !mb2RedirectedRef.current) {
      mb2RedirectedRef.current = true;
      redirectToMb2();
    }
  }, [status, isReady, onStepChange, canRedirect, redirectToRegister, canRedirectMb2, redirectToMb2, track, identify, user, error, email]);
  const isLoading = status === 'sending' || status === 'verifying';

  // A non-migrated account with no redirect target must not be left in limbo:
  // surface a message on whichever step it was rejected on instead of silently
  // doing nothing.
  const mb2Blocked = status === 'mb2-migration-required' && !canRedirectMb2;
  const rawEmailError = status === 'unauthenticated' ? error : null;
  const emailErrorKey = rawEmailError ? ERROR_TYPES[rawEmailError] : null;
  const emailError = mb2Blocked ? t('mb2MigrationRequired') : emailErrorKey ? t(emailErrorKey as never) : rawEmailError;
  const rawOtpError = status === 'awaiting-otp' || status === 'unauthenticated' ? error : null;
  const otpErrorKey = rawOtpError ? ERROR_TYPES[rawOtpError] : null;
  const otpError = mb2Blocked ? t('mb2MigrationRequired') : otpErrorKey ? t(otpErrorKey as never) : rawOtpError;
  function emailSubmitHandler(submittedEmail: string) {
    setEmail(submittedEmail);
    clearError();
    initiateLogin(submittedEmail);
  }
  function otpSubmitHandler(otp: string) {
    clearError();
    verifyOtp(email, otp);
  }
  function resendHandler() {
    clearError();
    initiateLogin(email);
  }
  return {
    email,
    isLoading,
    emailError,
    otpError,
    clearError,
    emailSubmitHandler,
    otpSubmitHandler,
    resendHandler
  };
}